import React, { createContext, useContext, useCallback, useMemo, ReactNode, useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { isSettingsAdminSiteControlsExperimentEnabled } from "../../experiment/experiments";
import { useSite } from "../../hooks/useSite";
import { logError } from "../../utils/logger";
import { postMessageToParent } from "../../message-bus/postMessageToParent";
import {
  getRovoExtensionAdminSettings,
  saveRovoExtensionAdminSettings,
  RovoExtAdminSettings,
} from "../gateway-client/gatewayClient";
import {
  SettingsContextValue,
  RemoteAdminSettingsError,
  RemoteAdminSettingsErrorType,
  UpdateRemoteSettingParams,
  LocalStoredSettings,
  LocalStoredSettingsKey,
  UpdateLocalStoredSettingParams,
  ChromeStoredSettings,
  ChromeStoredSettingsKey,
  UpdateChromeStoredSettingParams,
} from "./types";
import { LocalStorageKey } from "../localStorage";
import { useStateWithSync } from "../../hooks/useStateWithSync";
import {
  DEFAULT_CHROME_STORED_SETTINGS,
  DEFAULT_LOCAL_STORED_SETTINGS,
  DEFAULT_REMOTE_ADMIN_SETTINGS,
  DEFAULT_USER_SETTINGS,
} from "./constants";
import type { DefaultedRemoteAdminSettings } from "./types";

export enum SettingsQueryKeys {
  REMOTE_ADMIN_SETTINGS = "remoteAdminSettings",
}

/**
 * Checks chrome.storage.local for cached remote admin settings via message bus
 */
function getCachedRemoteAdminSettings(siteId: string): Promise<RovoExtAdminSettings | null> {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      window.removeEventListener("message", messageHandler);
      resolve(null);
    }, 2000);

    const messageHandler = (event: MessageEvent): void => {
      if (event.data.type === "GET_REMOTE_ADMIN_SETTINGS_CACHE_RESPONSE") {
        clearTimeout(timeoutId);
        window.removeEventListener("message", messageHandler);
        resolve(event.data.data);
      }
    };

    window.addEventListener("message", messageHandler, false);
    postMessageToParent({ type: "GET_REMOTE_ADMIN_SETTINGS_CACHE", siteId });
  });
}

/**
 * Updates the chrome.storage.local cache with new settings
 */
function setCachedRemoteAdminSettings(siteId: string, settings: RovoExtAdminSettings): void {
  postMessageToParent({
    type: "SET_REMOTE_ADMIN_SETTINGS_CACHE",
    siteId,
    settings,
  });
}

/**
 * Clears the chrome.storage.local cache to force a fresh API fetch
 * Returns a promise that resolves after a short delay to allow the message to be processed
 */
function clearCachedRemoteAdminSettings(siteId: string): Promise<void> {
  postMessageToParent({
    type: "CLEAR_REMOTE_ADMIN_SETTINGS_CACHE",
    siteId,
  });
  // Wait a short time for the message to be processed by the parent
  return new Promise((resolve) => setTimeout(resolve, 100));
}

/**
 * Fetches remote admin settings with chrome.storage.local caching
 */
async function fetchRemoteAdminSettingsWithCache(siteId: string): Promise<RovoExtAdminSettings> {
  const cachedData = await getCachedRemoteAdminSettings(siteId);

  if (cachedData !== null) {
    return cachedData;
  }

  const freshData = await getRovoExtensionAdminSettings(siteId);
  setCachedRemoteAdminSettings(siteId, freshData);

  return freshData;
}

const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

export interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider = ({ children }: SettingsProviderProps): React.JSX.Element => {
  const isSettingsAdminSiteControlsEnabled = isSettingsAdminSiteControlsExperimentEnabled();

  const [chromeStoredSettings, setChromeStoredSettings] =
    useState<ChromeStoredSettings>(DEFAULT_CHROME_STORED_SETTINGS);

  useEffect(() => {
    if (!isSettingsAdminSiteControlsEnabled) {
      return;
    }

    const messageHandler = (event: MessageEvent): void => {
      switch (event.data.type) {
        case "GET_OPTIONS_RESPONSE":
        case "SET_OPTIONS_RESPONSE": {
          setChromeStoredSettings((prev) => ({
            ...prev,
            ...event.data.options,
          }));
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener("message", messageHandler, false);
    postMessageToParent({ type: "GET_OPTIONS" });

    return (): void => {
      window.removeEventListener("message", messageHandler);
    };
  }, [isSettingsAdminSiteControlsEnabled]);

  const updateChromeStoredSetting = useCallback(
    <K extends ChromeStoredSettingsKey>(params: UpdateChromeStoredSettingParams<K>): void => {
      if (!isSettingsAdminSiteControlsEnabled) {
        return;
      }

      setChromeStoredSettings((prev) => ({
        ...prev,
        [params.key]: params.value,
      }));

      postMessageToParent({
        type: "SET_OPTIONS",
        updated: {
          [params.key]: params.value,
        },
      });
    },
    [isSettingsAdminSiteControlsEnabled],
  );

  const [welcomeHeaderEnabled, setWelcomeHeaderEnabled] = useStateWithSync(
    LocalStorageKey.WELCOME_HEADER_ENABLED,
    true,
    isSettingsAdminSiteControlsEnabled,
  );
  const [globalCreateEnabled, setGlobalCreateEnabled] = useStateWithSync(
    LocalStorageKey.GLOBAL_CREATE_ENABLED,
    true,
    isSettingsAdminSiteControlsEnabled,
  );
  const [localDevBannerEnabled, setLocalDevBannerEnabled] = useStateWithSync(
    LocalStorageKey.LOCAL_DEV_BANNER_ENABLED,
    true,
    isSettingsAdminSiteControlsEnabled,
  );
  const [calendarEnabled, setCalendarEnabled] = useStateWithSync(
    LocalStorageKey.CALENDAR_ENABLED,
    false,
    isSettingsAdminSiteControlsEnabled,
  );
  const [recentWorkEnabled, setRecentWorkEnabled] = useStateWithSync(
    LocalStorageKey.RECENT_WORK_ENABLED,
    true,
    isSettingsAdminSiteControlsEnabled,
  );
  const [bookmarksEnabled, setBookmarksEnabled] = useStateWithSync(
    LocalStorageKey.BOOKMARKS_ENABLED,
    true,
    isSettingsAdminSiteControlsEnabled,
  );
  const [announcementsEnabled, setAnnouncementsEnabled] = useStateWithSync(
    LocalStorageKey.ANNOUNCEMENTS_ENABLED,
    true,
    isSettingsAdminSiteControlsEnabled,
  );

  const localStoredSettings = useMemo<LocalStoredSettings>(
    () => ({
      [LocalStorageKey.WELCOME_HEADER_ENABLED]: welcomeHeaderEnabled,
      [LocalStorageKey.GLOBAL_CREATE_ENABLED]: globalCreateEnabled,
      [LocalStorageKey.LOCAL_DEV_BANNER_ENABLED]: localDevBannerEnabled,
      [LocalStorageKey.CALENDAR_ENABLED]: calendarEnabled,
      [LocalStorageKey.RECENT_WORK_ENABLED]: recentWorkEnabled,
      [LocalStorageKey.BOOKMARKS_ENABLED]: bookmarksEnabled,
      [LocalStorageKey.ANNOUNCEMENTS_ENABLED]: announcementsEnabled,
    }),
    [
      welcomeHeaderEnabled,
      globalCreateEnabled,
      localDevBannerEnabled,
      calendarEnabled,
      recentWorkEnabled,
      bookmarksEnabled,
      announcementsEnabled,
    ],
  );

  const updateLocalStoredSetting = useCallback(
    <K extends LocalStoredSettingsKey>(params: UpdateLocalStoredSettingParams<K>): void => {
      // Consumers must gate usage; keep this safe even if accidentally called.
      if (!isSettingsAdminSiteControlsEnabled) {
        return;
      }

      switch (params.key) {
        case LocalStorageKey.WELCOME_HEADER_ENABLED:
          setWelcomeHeaderEnabled(params.value);
          return;
        case LocalStorageKey.GLOBAL_CREATE_ENABLED:
          setGlobalCreateEnabled(params.value);
          return;
        case LocalStorageKey.LOCAL_DEV_BANNER_ENABLED:
          setLocalDevBannerEnabled(params.value);
          return;
        case LocalStorageKey.CALENDAR_ENABLED:
          setCalendarEnabled(params.value);
          return;
        case LocalStorageKey.RECENT_WORK_ENABLED:
          setRecentWorkEnabled(params.value);
          return;
        case LocalStorageKey.BOOKMARKS_ENABLED:
          setBookmarksEnabled(params.value);
          return;
        case LocalStorageKey.ANNOUNCEMENTS_ENABLED:
          setAnnouncementsEnabled(params.value);
          return;
        default:
          return;
      }
    },
    [
      isSettingsAdminSiteControlsEnabled,
      setWelcomeHeaderEnabled,
      setGlobalCreateEnabled,
      setLocalDevBannerEnabled,
      setCalendarEnabled,
      setRecentWorkEnabled,
      setBookmarksEnabled,
      setAnnouncementsEnabled,
    ],
  );
  const siteContext = useSite({ allowUndefined: true });
  const siteId = siteContext ? siteContext.site.workspace.cloudId : undefined;
  const queryClient = useQueryClient();
  const [remoteAdminSettingsMutationError, setRemoteAdminSettingsMutationError] =
    useState<RemoteAdminSettingsError | null>(null);

  const {
    data: remoteAdminSettings = DEFAULT_REMOTE_ADMIN_SETTINGS,
    isLoading: isLoadingRemoteAdminSettings,
    error: queryErrorRaw,
  } = useQuery<RovoExtAdminSettings, Error>({
    queryKey: [SettingsQueryKeys.REMOTE_ADMIN_SETTINGS, siteId],
    queryFn: () => {
      if (!siteId) {
        throw new Error("siteId is required to fetch remote admin settings");
      }
      return fetchRemoteAdminSettingsWithCache(siteId);
    },
    enabled: isSettingsAdminSiteControlsEnabled && !!siteId,
    staleTime: 60 * 1000, // Re-check chrome.storage to hand off caching decisions
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 1,
    throwOnError: false,
  });

  const previousErrorRef = useRef<Error | null>(null);

  // Handle error logging once when error state changes
  useEffect(() => {
    if (queryErrorRaw && queryErrorRaw !== previousErrorRef.current) {
      logError({ error: queryErrorRaw, impact: "Failed to fetch remote admin settings" });
      previousErrorRef.current = queryErrorRaw;
    }
  }, [queryErrorRaw]);

  const remoteAdminSettingsQueryError = useMemo<RemoteAdminSettingsError | null>(() => {
    if (!queryErrorRaw) {
      return null;
    }
    return new RemoteAdminSettingsError({
      type: RemoteAdminSettingsErrorType.QUERY_ERROR,
      originalError: queryErrorRaw,
    });
  }, [queryErrorRaw]);

  const mutation = useMutation<
    RovoExtAdminSettings,
    Error,
    Partial<RovoExtAdminSettings>,
    { previousSettings: RovoExtAdminSettings | undefined }
  >({
    mutationFn: async (data) => {
      if (!siteId) {
        throw new Error("siteId is required to update remote admin settings");
      }
      return saveRovoExtensionAdminSettings(siteId, data);
    },
    onMutate: async (newData) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: [SettingsQueryKeys.REMOTE_ADMIN_SETTINGS, siteId],
      });

      // Snapshot the previous value.
      // NOTE: React Query cache may be empty on first load; keep a concrete fallback so we can always rollback.
      const previousSettingsFromCache = queryClient.getQueryData<RovoExtAdminSettings>([
        SettingsQueryKeys.REMOTE_ADMIN_SETTINGS,
        siteId,
      ]);
      const previousSettings = previousSettingsFromCache ?? DEFAULT_REMOTE_ADMIN_SETTINGS;

      // Optimistically update to the new value
      const optimisticSettings: RovoExtAdminSettings = {
        ...previousSettings,
        ...newData,
      };
      queryClient.setQueryData<RovoExtAdminSettings>(
        [SettingsQueryKeys.REMOTE_ADMIN_SETTINGS, siteId],
        optimisticSettings,
      );

      // Return context with the previous value for rollback
      return { previousSettings };
    },
    onError: (originalError, newData, context) => {
      // Rollback to previous value on error
      queryClient.setQueryData<RovoExtAdminSettings>(
        [SettingsQueryKeys.REMOTE_ADMIN_SETTINGS, siteId],
        context?.previousSettings ?? DEFAULT_REMOTE_ADMIN_SETTINGS,
      );

      const settingKey = Object.keys(newData)[0] as keyof RovoExtAdminSettings;
      const error = new RemoteAdminSettingsError({
        type: RemoteAdminSettingsErrorType.MUTATION_ERROR,
        originalError,
        context: {
          settingKey,
          attemptedValue: Object.values(newData)[0],
          previousValue: context?.previousSettings?.[settingKey],
        },
      });

      setRemoteAdminSettingsMutationError(error);
      logError({ error: originalError, impact: "Failed to update remote admin settings" });
    },
    onSuccess: (data) => {
      // Update chrome.storage.local so other tabs immediately see the new settings
      if (siteId) {
        setCachedRemoteAdminSettings(siteId, data);
      }
    },
    onSettled: () => {
      // Refetch to ensure we have the latest data
      void queryClient.invalidateQueries({
        queryKey: [SettingsQueryKeys.REMOTE_ADMIN_SETTINGS, siteId],
      });
    },
  });

  const updateRemoteAdminSettings = useCallback(
    async <K extends keyof RovoExtAdminSettings>(params: UpdateRemoteSettingParams<K>): Promise<void> => {
      if (!isSettingsAdminSiteControlsEnabled || !siteId) {
        return;
      }

      // Clear previous mutation error when starting a new mutation
      setRemoteAdminSettingsMutationError(null);

      try {
        await mutation.mutateAsync({
          [params.key]: params.value,
        } as Partial<RovoExtAdminSettings>);
      } catch {
        // Error is handled by onError callback, but we need to catch it here
        // to prevent unhandled promise rejection
      }
    },
    [isSettingsAdminSiteControlsEnabled, siteId, mutation],
  );

  const refreshRemoteAdminSettings = useCallback(async (): Promise<void> => {
    if (!isSettingsAdminSiteControlsEnabled || !siteId) {
      return;
    }
    // Step 1: Clear chrome.storage.local cache and wait for it to be processed
    await clearCachedRemoteAdminSettings(siteId);
    // Step 2: Invalidate React Query state, triggering a refetch
    await queryClient.invalidateQueries({
      queryKey: [SettingsQueryKeys.REMOTE_ADMIN_SETTINGS, siteId],
    });
  }, [isSettingsAdminSiteControlsEnabled, siteId, queryClient]);

  const effectiveRemoteAdminSettings: DefaultedRemoteAdminSettings = useMemo(
    () => ({
      ...DEFAULT_REMOTE_ADMIN_SETTINGS,
      ...remoteAdminSettings,
    }),
    [remoteAdminSettings],
  );

  const syncAdminControlledFeaturesToChrome = useCallback((settings: RovoExtAdminSettings): void => {
    const definitionsAppEnabled = settings.controlledUserGdocsDefinitionsEnabled !== false;

    postMessageToParent({
      type: "SET_ADMIN_CONTROLLED_FEATURES",
      features: {
        definitionsAppEnabled,
      },
    });
  }, []);

  useEffect(() => {
    if (isLoadingRemoteAdminSettings) {
      return;
    }
    syncAdminControlledFeaturesToChrome(effectiveRemoteAdminSettings);
  }, [effectiveRemoteAdminSettings, isLoadingRemoteAdminSettings, syncAdminControlledFeaturesToChrome]);

  const previousControlledUserNtpEnabledRef = useRef<RovoExtAdminSettings["controlledUserNtpEnabled"]>(undefined);
  const previousControlledUserCompanionEnabledRef =
    useRef<RovoExtAdminSettings["controlledUserRovoCompanionEnabled"]>(undefined);
  const previousControlledUserCompanyAnnouncementsRef =
    useRef<RovoExtAdminSettings["controlledUserCompanyAnnouncements"]>(undefined);

  useEffect(() => {
    if (isLoadingRemoteAdminSettings) {
      return;
    }

    const currentNtp = effectiveRemoteAdminSettings.controlledUserNtpEnabled;
    const previousNtp = previousControlledUserNtpEnabledRef.current;

    // Admin disables NTP for all users (transition to false)
    if (currentNtp === false && previousNtp !== false) {
      postMessageToParent({
        type: "SET_OPTIONS",
        updated: {
          ntpEnabled: false,
        },
      });
    }

    // Admin re-enables NTP for all users (false -> true/undefined)
    if (previousNtp === false && currentNtp !== false) {
      postMessageToParent({
        type: "SET_OPTIONS",
        updated: {
          ntpEnabled: DEFAULT_USER_SETTINGS.ntpEnabled,
        },
      });
    }

    previousControlledUserNtpEnabledRef.current = currentNtp;
  }, [effectiveRemoteAdminSettings.controlledUserNtpEnabled, isLoadingRemoteAdminSettings]);

  useEffect(() => {
    if (isLoadingRemoteAdminSettings) {
      return;
    }

    const currentCompanion = effectiveRemoteAdminSettings.controlledUserRovoCompanionEnabled;
    const previousCompanion = previousControlledUserCompanionEnabledRef.current;

    // Admin disables Companion for all users (transition to false)
    if (currentCompanion === false && previousCompanion !== false) {
      postMessageToParent({
        type: "SET_OPTIONS",
        updated: {
          rovoCompanionEnabled: false,
        },
      });
    }

    // Admin re-enables Companion for all users (false -> true/undefined)
    if (previousCompanion === false && currentCompanion !== false) {
      postMessageToParent({
        type: "SET_OPTIONS",
        updated: {
          rovoCompanionEnabled: DEFAULT_USER_SETTINGS.rovoCompanionEnabled,
        },
      });
    }

    previousControlledUserCompanionEnabledRef.current = currentCompanion;
  }, [effectiveRemoteAdminSettings.controlledUserRovoCompanionEnabled, isLoadingRemoteAdminSettings]);

  useEffect(() => {
    if (isLoadingRemoteAdminSettings) {
      return;
    }

    const currentAnnouncements = effectiveRemoteAdminSettings.controlledUserCompanyAnnouncements;
    const previousAnnouncements = previousControlledUserCompanyAnnouncementsRef.current;

    // Admin disables Company Announcements for all users (transition to false)
    if (currentAnnouncements === false && previousAnnouncements !== false) {
      setAnnouncementsEnabled(false);
    }

    // Admin re-enables Company Announcements for all users (false -> true/undefined)
    if (previousAnnouncements === false && currentAnnouncements !== false) {
      setAnnouncementsEnabled(DEFAULT_LOCAL_STORED_SETTINGS[LocalStorageKey.ANNOUNCEMENTS_ENABLED]);
    }

    previousControlledUserCompanyAnnouncementsRef.current = currentAnnouncements;
  }, [
    effectiveRemoteAdminSettings.controlledUserCompanyAnnouncements,
    isLoadingRemoteAdminSettings,
    setAnnouncementsEnabled,
  ]);

  const contextValue = useMemo<SettingsContextValue>(
    () => ({
      remoteAdminSettings: effectiveRemoteAdminSettings,
      updateRemoteAdminSettings,
      refreshRemoteAdminSettings,
      isLoadingRemoteAdminSettings,
      remoteAdminSettingsQueryError,
      remoteAdminSettingsMutationError,
      localStoredSettings,
      updateLocalStoredSetting,
      chromeStoredSettings,
      updateChromeStoredSetting,
    }),
    [
      effectiveRemoteAdminSettings,
      updateRemoteAdminSettings,
      refreshRemoteAdminSettings,
      isLoadingRemoteAdminSettings,
      remoteAdminSettingsQueryError,
      remoteAdminSettingsMutationError,
      localStoredSettings,
      updateLocalStoredSetting,
      chromeStoredSettings,
      updateChromeStoredSetting,
    ],
  );

  // If feature gate is disabled, still provide context but with defaults
  if (!isSettingsAdminSiteControlsEnabled) {
    const defaultValue: SettingsContextValue = {
      remoteAdminSettings: DEFAULT_REMOTE_ADMIN_SETTINGS,
      updateRemoteAdminSettings: async () => {},
      refreshRemoteAdminSettings: async () => {},
      isLoadingRemoteAdminSettings: false,
      remoteAdminSettingsQueryError: null,
      remoteAdminSettingsMutationError: null,
      localStoredSettings: DEFAULT_LOCAL_STORED_SETTINGS,
      updateLocalStoredSetting: () => {},
      chromeStoredSettings: DEFAULT_CHROME_STORED_SETTINGS,
      updateChromeStoredSetting: () => {},
    };
    return <SettingsContext.Provider value={defaultValue}>{children}</SettingsContext.Provider>;
  }

  return <SettingsContext.Provider value={contextValue}>{children}</SettingsContext.Provider>;
};

export const useSettings = (): SettingsContextValue => {
  const context = useContext(SettingsContext);

  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }

  return context;
};
